<template>
  <article class="mx-auto my-0 text-neutral-900 md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl desktop:my-5">
    <ProductList
      :id="pageId"
      :by-categories="categoryIds"
      :page="currentPage"
      :text-search="search"
      :category-tree="accordionCategories"
      :by-attributes="attributes"
      :scroll-to="scrollTo"
    />
  </article>
</template>

<script lang="ts" setup>
import ProductList from '~/components/Category/Magnolia/ProductList.vue';
import { useBreadcrumbs } from '~/composables/useBreadcrumb/useBreadcrumb';
import { useSSR } from '~/composables/useSSR/useSSR';

const route = useRoute();
const { result: categories, rootCategory } = useCategories();
const { result: category, load: loadCategory, setCategoryMeta } = useCategory();
const { breadcrumbs, build: buildBreadcrumbs } = useBreadcrumbs();
const { setCategoryInfo, setOverallInfo } = useSeo();
const { setHrefLangForCategory } = useHrefLang();
const { onSSR } = useSSR();

const {
  type,
  slug_1: slug1,
  slug_2: slug2,
  slug_3: slug3,
  slug_4: slug4,
} = route.params;

const { isNew, isSale, isSearch } = route.meta as { isNew: boolean; isSale: boolean; isSearch: boolean };
const { page = '1', search, filters } = route.query;

const scrollTo = ref();

const activeSlugs = [slug2, slug1].filter(item => !item?.includes('c_')).filter(Boolean) as string[];

const currentPage = Number.parseInt(page as string, 10);

const currentCategory = computed(() => {
  const catParam = route.query.categoryId;
  if (catParam) {
    return breadcrumbs.value?.find(({ id: breadcrumbId }) => breadcrumbId === catParam);
  }
  return breadcrumbs.value[breadcrumbs.value.length - 1];
});

const removeUndefined = (value: object) => JSON.parse(JSON.stringify(value));
const attributes = removeUndefined({ sale: isSale, recent: isNew });

const pageId = computed(() => `${[activeSlugs, type, page, search, filters, isSearch ? slug2 : null].filter(Boolean).join('-')}`);

const accordionCategories = computed(() => {
  // if not on search result page and not in root directory start with second-deepest category
  if (slug1 && !slug1.includes('c_') && !search) {
    const rootSlug = breadcrumbs.value[0]?.slug;
    return categories.value?.find(({ slug }) => slug === rootSlug);
  }

  if (isSale || isNew) {
    return { childrenItems: categories.value, ignoreRoot: true };
  }

  return { ...categories.value, ignoreRoot: true };
});

const setHrefLangForCurrentCategory = () => {
  if (category.value) {
    if (!slug2) {
      return setHrefLangForCategory(category.value?.slugAllLocales, undefined, isSale, isNew);
    }

    const parent = categories.value.find(cat => findTopParent(cat, slug1 as string));
    return setHrefLangForCategory(parent?.slugAllLocales, category.value?.slugAllLocales, isSale, isNew);
  }
};

const findTopParent = (category: NavbarCategory, searchedCategory: string): string | undefined => {
  if (category.slug === searchedCategory) {
    return category.slug;
  }

  if (!category.childrenItems) {
    return undefined;
  }

  return category.childrenItems?.find(item => !!findTopParent(item, searchedCategory))?.slug;
};

watch(categories, async (newVal) => {
  if (newVal) {
    buildBreadcrumbs();
  }
});

watch(category, async (newVal) => {
  if (newVal) {
    setCategoryMeta(category.value);
    setCategoryInfo(currentCategory);
    setHrefLangForCurrentCategory();
  }
});

onSSR(async () => {
  if (import.meta.server) {
    await loadCategory();
  }

  buildBreadcrumbs();
  setCategoryMeta(category.value);
  setCategoryInfo(currentCategory);
  const topLevelCategories = categories.value.map(cat => cat.slug); // ex: damen | herren | jungen | ...
  topLevelCategories.push(rootCategory.value.slug);

  if (slug1 && !slug1.includes('c_') && !topLevelCategories?.includes(slug1 as string)) {
    const mainCategory = categories.value.find(cat => findTopParent(cat, slug1 as string));
    if (!mainCategory) {
      navigateTo('/', { redirectCode: 301 });
      return;
    }

    if (mainCategory && mainCategory.slug !== slug1) {
      navigateTo(`/c/${mainCategory.slug}/${slug1}`, { redirectCode: 301 });
    }
  }

  if ((slug3 && !slug3.includes('c_')) || (slug4 && !slug4.includes('c_'))) {
    navigateTo(`/c/${slug1}/${slug2}`, { redirectCode: 301 });
  }
  setHrefLangForCurrentCategory();
});

const categoryIds = computed(() => (category.value?.id ? [category.value.id] : []));

setCategoryInfo(currentCategory);

onBeforeUnmount(() => {
  setOverallInfo();
});
</script>
